// i18n/en.ts
export const en = {
  theme: {
    label: 'Color theme',
    operator: 'Operator',
    midnight: 'Midnight',
    ember: 'Ember',
  },
  app: {
    title: 'V12 Quantitative Trading System',
    reconnect: 'Reconnect',
    lastUpdate: 'Updated',
  },
  nav: {
    dashboard: 'Dashboard',
    'dashboard-v12': 'V12 Dashboard',
    tradier: 'Tradier',
    'realtime-scoring': 'Realtime Scoring',
    broker: 'Broker',
    'paper-audit': 'Paper Audit',
    audit: 'Audit',
    admin: 'Admin',
    settings: 'Settings',
    databases: 'Databases',
    'auto-trading': 'Auto Trading',
    logout: 'Sign out',
    logoutConfirm: 'Sign out of the dashboard?',
  },
  auditHistory: {
    title: 'Audit History',
    subtitle: 'R10 live execution decision log · PostgreSQL / Kafka',
    refresh: 'Refresh',
    autoRefresh: 'Auto-refresh (15s)',
    recordsPrefix: 'Total ',
    recordsSuffix: ' records',
    buyScores: 'Buy scores',
    sellScores: 'Sell scores',
    priceRecords: 'Price records',
    scoreBackfills: 'Score backfills',
    symbolPlaceholder: 'Symbol (e.g. AAPL)',
    allDecisions: 'All decisions',
    clearFilters: 'Clear filters',
    loadFailed: 'Load failed',
    loading: 'Loading...',
    columns: {
      time: 'Time',
      symbol: 'Symbol',
      side: 'Side',
      decision: 'Decision',
      stage: 'Stage',
      scores: 'Buy/Sell score',
      priceChange: 'Price/Change',
      message: 'Message',
      orderId: 'Order ID',
    },
    noMatches: 'No matching records',
    noRecords: 'No audit records yet',
    sides: { buy: 'Buy', sell: 'Sell' },
    stages: {
      risk_gate: 'Risk gate',
      plan: 'Plan',
      broker: 'Broker',
      signal: 'Signal',
    },
    scoreLabels: { buy: 'Buy', sell: 'Sell', score: 'Score' },
    priceLabels: { limit: 'Limit', cost: 'Cost', peak: 'Peak' },
    backfilledBy: (source: string) => `filled from ${source}`,
    showing: (from: number, to: number, total: number) => `Showing ${from}–${to} / ${total}`,
    previous: 'Previous',
    next: 'Next',
    source: 'Source',
    sourceDetail: 'Kafka audit projection · refreshes every 15 seconds · newest data first · EMITTED rows backfill trigger scores by intent_id',
  },
  scores: {
    title: 'Score Ranking',
  },
  realtime: {
    // Realtime scoring page + LiveScoresV2Panel (LiveScoresV2Panel.tsx, RealtimeScoring.tsx).
    pageTitle: 'Realtime Scoring',
    pageSubtitle: 'Top Buy · Top Sell — single-writer v13 SSOT',
    ownershipTag: 'topn:live_scores · sole-writer scoring_v13',
    panelTitle: (n: number) => `Top ${n} · realtime`,
    updatedAt: (time: string) => `updated ${time}`,
    sseLive: 'SSE live',
    loading: 'loading…',
    loadingVector: 'loading vector…',
    errorPrefix: 'error',
    noData: 'no data',
    leaderboard: {
      symbol: 'symbol',
      session: 'session',
      live: 'live',
      bar: 'bar',
      gap: 'gap',
      microprice: 'microprice',
    },
    heatmap: {
      title: '15-field SignalVector',
      emptyHint: 'click a row in the leaderboard to inspect',
      composite: 'composite',
      groups: {
        quote: 'Quote',
        trade: 'Trade',
        summary: 'Summary',
        timesale: 'Timesale',
        session: 'Session',
      },
      dimLabels: {
        spread_bps: 'Spread bps',
        depth_imbalance: 'Depth Imb.',
        microprice: 'Microprice',
        nbbo_staleness_score: 'NBBO Staleness',
        nbbo_at_print_bps: 'Print NBBO',
        flow_delta: 'Flow Δ',
        vol_burst: 'Vol Burst',
        vol_rate_session: 'Volume Ratio',
        turnover_rate_score: 'Turnover',
        trade_intensity: 'Trade Intensity',
        range_pos: 'Range Pos.',
        gap_open_score: 'Gap Open',
        cancel_rate: 'Cancel Rate',
        tape_quality: 'Tape Quality',
      },
    },
    decisions: {
      title: 'realtime:decisions',
      subtitle: (limit: number) => `last ${limit}`,
      empty: 'no decisions yet',
    },
    metrics: {
      title: 'soak metrics',
      tick: (ms: number) => `tick ${ms}ms`,
      invariantViolation: 'invariant violation',
      brokerSubmitViolation: (count: number) => `broker_submit_count = ${count} (must be 0 in dry-run)`,
      dimCountViolation: (count: number) => `v14_signal_dim_count = ${count} (expected 15)`,
      sections: {
        orch: 'orch',
        autoTrader: 'auto_trader',
        tradierWs: 'tradier_ws',
      },
      labels: {
        symbol_count: 'Symbol count',
        v14_signal_dim_count: 'V14 signal dim count',
        decision_count: 'Decision count',
        broker_submit_count: 'Broker submit count',
        ws_reconnect_count: 'Ws reconnect count',
      },
      symbolsActive: (count: number) => `${count} symbols active in orchestrator`,
      orchestratorIdle: 'orchestrator idle',
    },
    lifecycle: {
      title: 'sidechain lifecycle',
      loading: 'lifecycle: loading…',
      errorPrefix: 'lifecycle',
      frozenMirror: 'FROZEN MIRROR',
      frozen: 'frozen',
      live: 'live',
      fsmPrefix: 'FSM',
      fsm: 'FSM',
      regime: 'regime',
      freeze: 'freeze',
    },
  },
  gate: {
    pass: 'PASS',
    wait: 'WAIT',
    lock: 'LOCK',
  },
  market: {
    title: 'Market Tower',
    symbols: 'Symbols',
    movers: 'Movers',
    watchlist: 'Watchlist',
    watchlistHint: 'Ranking Universe — not your user watchlist (find yours in Broker → Watchlists)',
    topMovers: 'Top Movers',
    breadthUp: 'Up',
    breadthDown: 'Down',
    breadthFlat: 'Flat',
  },
  broker: {
    title: 'Account',
    equity: 'Total Equity',
    unrealizedPnl: 'Unrealized P&L',
    brokers: 'Brokers',
    connected: 'Connected',
    disconnected: 'Disconnected',
    positions: 'Positions',
    orders: 'Orders',
    qty: 'Qty',
    costBasis: 'Cost',
    stopLoss: 'Stop',
    marketValue: 'Mkt Val',
    unrealizedPnlShort: 'Unreal P&L',
    // 2026-06-21: pane headers in the broker page (frontend/src/pages/Broker.tsx
    // lines 427 and 435). These were hardcoded English ("Production / Live"
    // and "Sandbox / Paper") and never translated when the outer headline
    // ("📊 模拟账户" / "📈 真实账户") was migrated to i18n. The two strings
    // // coexist intentionally: the headline describes the runtime mode, the
    // // pane label names the destination / account kind.
    paneLive: 'Production / Live',
    panePaper: 'Sandbox / Paper',
  },
  signals: {
    title: 'Signals',
    top: 'Top Signals',
    buy: 'Buy Signals',
    sell: 'Sell Signals',
  },
  execution: {
    title: 'Execution',
    fillRate: 'Fill Rate',
    totalOrders: 'Total Orders',
    filled: 'Filled',
    rejected: 'Rejected',
    activeOrders: 'Active Orders',
  },
  metrics: {
    title: 'System Metrics',
    eventsPerSec: 'Events/sec',
    quotes: 'Quotes',
    orders: 'Orders',
    quoteDelay: 'Quote Delay',
    fillRate: 'Fill Rate',
    rejects: 'Rejects',
  },
  alerts: {
    title: 'Alerts',
    all: 'All',
    critical: 'Critical',
    warning: 'Warning',
    noActive: 'No active alerts',
  },
  admin: {
    title: 'Admin',
    refresh: 'Refresh',
    system: 'System',
    adapters: 'Adapters',
    services: 'Services',
    projections: 'Projections',
    running: 'Running',
    stopped: 'Stopped',
    connected: 'Connected',
    disconnected: 'Disconnected',
  },
  settings: {
    language: 'Language',
    websocket: 'WebSocket',
    eventLog: 'Event Log',
    checkpoints: 'Checkpoints',
    totalEvents: 'Total Events',
    lastSeq: 'Last Seq',
    noCheckpoints: 'No checkpoints',
    restore: 'Restore',
    loading: 'Loading...',
    save: 'Save',
    saved: 'Saved',
    saving: 'Saving...',
    failed: 'Failed',
    show: 'Show',
    hide: 'Hide',
    hotReloadNote: 'Settings reload on save',
    riskParams: 'Risk Parameters',
    maxSinglePosition: 'Max Single Position',
    maxTotalExposure: 'Max Total Exposure',
    maxOrdersPerDay: 'Max Orders / Day',
    maxHoldTime: 'Max Hold Time',
    stopLoss: 'Stop Loss',
    takeProfit: 'Take Profit',
    circuitBreakerDrawdown: 'Circuit Breaker Drawdown',
    dailyDrawdownKill: 'Daily Drawdown Kill',
    orderSize: 'Order Size',
    orderSizeOfPortfolio: '% of portfolio',
    execution: 'Execution',
    paperMode: 'Paper Mode',
    paperModeDesc: 'Simulated trading, no real orders',
    liveTradingEnabled: 'Enable Live Trading',
    liveTradingEnabledDesc: 'Real money orders — confirm broker is connected',
    tradier: 'Tradier',
    tradierAccount: 'Tradier Account',
    tradierApiKey: 'API Key',
    tradierSandboxApiKey: 'Sandbox API Key',
    tradierPaperAccount: 'Paper Account',
    tradierBaseUrl: 'Base URL',
    // 2026-06-22 build chain repair commit #3 — credential-specific
    // button states (distinct from generic settings.save/saved above).
    credSaved: 'Credential saved',
    credSave: 'Save credential',
    credSaving: 'Saving credential…',
    credShow: 'Show',
    credHide: 'Hide',
    configured: 'Configured',
    telegramTitle: 'Telegram Alert Credentials',
    telegramBotToken: 'Bot Token',
    telegramRotationFailed: 'Telegram token rotation failed',
    telegramRotationOpening: 'Opening the local hidden input',
    telegramTokenNotice: 'The token never passes through the browser. Paste the latest full BotFather reply into the local hidden input; the system validates the bot identity, updates atomically, restarts only the notifier, and verifies the loaded result.',
    telegramWaitingForInput: 'Waiting for local input…',
    telegramRotateToken: 'Replace token (local secure input)',
    telegramChatId: 'Chat ID',
    telegramSaveChatId: 'Save Chat ID',
    tradierCredentials: 'Tradier API Credentials',
    tradierRestartNotice: 'The .env update is active, but running trading processes cache environment variables at startup. Restart these services before the new credentials can affect order submission:',
    tradierRestartServices: 'quant-r10-execution-worker, quant-r10-order-status-watcher, quant-r10-broker-oracle, quant-r10-market-clock.',
    tradierProductionKey: 'API Key (production)',
    tradierLiveAccount: 'Account ID (live)',
    tradierSandboxAccount: 'Sandbox Account ID',
    mode: 'Mode',
    liveModeWarning: '⚠ live uses a real-money account. Paper uses sandbox REST with 15-minute delayed data and no streaming.',
  },
  errors: {
    crash: 'Something went wrong',
    retry: 'Retry',
  },
  components: {
    // 2026-06-22 build chain repair commit #3 — flat sections referenced
    // via t.components.{X} (NOT via t.components.tradier.{X} which is the
    // sub-namespace for the tradier sub-component). Keys are read directly
    // from useI18n().t.components.* in MarketSessionBadge / MarketTape /
    // PageShell / SystemResourcesBar / Broker.
    marketSession: {
      label: 'SESSION',
      states: {
        OPEN: 'Open',
        PRE_MARKET: 'Pre-market',
        AFTER_HOURS: 'After Hours',
        CLOSED: 'Closed',
        HALTED: 'Halted',
        UNKNOWN: 'Unknown',
      },
    },
    marketTape: {
      label: 'Tape',
      live: 'LIVE',
      stale: 'STALE',
      fear: 'FEAR',
      closed: 'closed',
    },
    pageShell: {
      loading: 'Loading…',
      reconnect: 'Reconnect',
      lastUpdate: 'Updated',
    },
    systemResources: {
      title: 'System Resources',
      cpu: 'CPU',
      cpuTitle: 'CPU {0}% across {1} cores',
      mem: 'MEM',
      memTitle: 'Memory {0} GB / {1} GB',
      load: 'LOAD',
      loadTitle: 'Load 1m: {0}',
      pipeline: 'Pipeline',
      pipelineTitle: 'Pipeline processes CPU usage',
    },
    broker: {
      // Read by Broker.tsx:196 `const br = t.components?.broker ?? {};`
      // All 16 keys are defensive — the page renders empty strings if any
      // is missing, but the TS errors block tsc.
      freeze: 'Freeze',
      freezeReason: 'Freeze reason',
      frozenDefault: 'frozen',
      goLive: 'View Live',
      goSandbox: 'View Sandbox',
      viewOnlyHint: 'View only — shows this account’s data; does NOT change the runtime trading account (that is the guarded /api/r10/access flow).',
      viewingBanner: '👁 viewing {0} · runtime trades on {1}',
      loadingRuntime: 'Loading runtime state…',
      pageBootHeadline: 'Boot',
      pageFrozenHeadline: 'Frozen',
      pageLiveHeadline: 'Live',
      pageSandboxHeadline: 'Sandbox',
      paneLive: 'Live',
      panePaper: 'Paper',
      showOtherAccount: 'Load other account',
      hideOtherAccount: 'Hide other account',
      runtimeState: 'Runtime state: {0}',
      transitionAccepted: 'Transition accepted: {0}',
      transitionFailed: 'Transition failed: {0}',
      unfreeze: 'Unfreeze',
    },
    metrics: {
      title: 'System Metrics',
      noData: 'No metrics data',
      eventsPerSec: 'Events/sec',
      quotes: 'Quotes',
      orders: 'Orders',
      fillRate: 'Fill Rate',
      rejects: 'Rejects',
      quoteDelay: 'Quote Delay',
    },
    scoreRanking: {
      title: 'Score Ranking',
      noSignals: 'No signals — inject market data',
      noBuySignals: 'No buy signals — inject market data',
      noSellSignals: 'Independent sell-score ranking not yet integrated',
      buyTitle: 'Buy Score Ranking',
      sellTitle: 'Sell Score Ranking',
      sellNotIntegrated: 'Independent sell-score ranking not yet integrated',
      buyThreshold: 'BUY threshold',
      sellThreshold: 'SELL threshold',
      symbol: 'Symbol',
      score: 'Score',
      signal: 'Signal',
      topN: 'Top 10',
      distHigh: 'From 52w High',
      changePct: 'Chg',
      freshnessRefreshing: 'Refreshing',
      freshnessUpdated: 'Updated',
      freshnessWarming: 'Warming up',
      freshnessAge: 'age',
      freshnessCompute: 'compute',
      freshnessStale: 'stale',
    },
    governance: {
      title: 'V12 Certification',
      g1: 'Event Layer',
      g2: 'Canonical Bar',
      g3: 'Score Layer',
      g4: 'Execution',
      architecture: 'Architecture',
      model: 'Model',
    },
    databases: {
      title: 'Databases',
      questdb: 'QuestDB',
      duckdb: 'DuckDB',
      status: 'Status',
      connected: 'Connected',
      disconnected: 'Disconnected',
      error: 'Error',
      tables: 'Tables',
      host: 'Host',
      ilpPort: 'ILP Port',
      httpPort: 'HTTP Port',
      noTables: 'No tables',
      refresh: 'Refresh',
      start: 'Start',
      stop: 'Stop',
      // 2026-06-12 multi-DB extension
      kind: 'Kind',
      version: 'Version',
      uptime: 'Uptime',
      path: 'Path',
      rows: 'Rows',
      size: 'Size',
      file: 'File',
      namespace: 'Namespace',
      keys: 'Keys',
      memory: 'Memory',
      clients: 'Clients',
      retired: 'Retired',
      empty: 'Empty',
      processes: 'Processes',
      summary: 'Summary',
      tablesCount: 'Tables',
      pid: 'PID',
      sampledKeys: 'Sampled keys',
      noFiles: 'No files',
      noDetail: 'No detail',
      loading: 'Loading…',
      symbols: 'Symbols',
      timeColumn: 'Time column',
      oldest: 'Oldest',
      newest: 'Newest',
      freshness: 'Freshness',
      quality: 'Quality',
      freshToday: 'Today',
      freshRecent: 'Recent',
      freshStale: 'Stale',
      freshOld: 'Old',
      backends: 'data systems',
      topics: 'Topics',
      topic: 'Topic',
      partitions: 'Partitions',
      messages: 'Messages',
      brokers: 'Brokers',
      mode: 'Mode',
      replicationFactor: 'Min RF',
      minimumIsr: 'Min ISR',
      offsetRange: 'Offset range',
      shadowHealth: 'Shadow pipeline health',
      backlog: 'Backlog',
      claimed: 'Claimed',
      poison: 'Poison',
      oldestBacklog: 'Oldest backlog',
      healthTtl: 'Health TTL',
      lag: 'Lag',
      consumerGroup: 'Audit consumer group',
      commitOn: 'commit ON',
      commitOff: 'commit OFF',
      runtimeAuthority: 'Runtime authority',
      executionAuthority: 'Execution authority',
      tradingGate: 'Trading readiness gate',
      noTopics: 'No business topics',
      kafkaBoundary: 'Kafka is the event transport, not the trading truth database.',
      kafkaBoundaryDetail: 'PostgreSQL remains durable execution truth. Kafka availability never gates the existing trading runtime.',
    },
    tradier: {
      title: 'Tradier Brokerage',
      refresh: 'Refresh',
      cachedSnapshotRefreshing: 'Showing cached broker data while refreshing',
      marketOpen: 'Market Open',
      marketClosed: 'Market Closed',
      accountId: 'Account',
      // Overview
      totalEquity: 'Total Equity',
      cash: 'Cash',
      buyingPower: 'Buying Power',
      unrealizedPnl: 'Unrealized P&L',
      portfolioValue: 'Portfolio Value',
      dayTrades: 'Day Trades',
      accountStatus: 'Status',
      // Positions
      positions: 'Open Positions',
      noPositions: 'No open positions',
      qty: 'Qty',
      costBasis: 'Cost Basis',
      mktValue: 'Mkt Value',
      currentPrice: 'Current Price',
      unrealPnl: 'Unreal. P&L',
      pnlPct: 'P&L %',
      dateAcquired: 'Date Acquired',
      buyAction: 'Buy',
      sellAction: 'Sell',
      buyToOpen: 'Buy to Open',
      sellToClose: 'Sell to Close',
      buyToClose: 'Buy to Close',
      sellToOpen: 'Sell to Open',
      sellShort: 'Sell Short',
      buyToCover: 'Buy to Cover',
      orderStatus: {
        open: 'Open',
        pending: 'Pending',
        submitted: 'Submitted',
        partiallyFilled: 'Partial fill',
        filled: 'Filled',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
        expired: 'Expired',
        pendingCancel: 'Pending cancel',
      },
      prefillOrder: 'Trade ticket loaded from position',
      // Orders
      orders: 'Orders',
      noOrders: 'No orders',
      placeOrder: 'Place Order',
      cancelOrder: 'Cancel',
      orderId: 'Order ID',
      side: 'Side',
      type: 'Type',
      duration: 'Duration',
      price: 'Price',
      filledQty: 'Filled Qty',
      avgPrice: 'Avg Price',
      created: 'Created',
      filledTime: 'Filled at',
      acquired: 'Opened',
      market: 'Market',
      limit: 'Limit',
      stopLimit: 'Stop Limit',
      durationPre: 'Pre-market',
      durationPost: 'Post-market',
      liveManualBlocked: 'Direct live placement is disabled. Use the sandbox account for manual Tradier orders, or R10 auto-trading for production.',
      sessionClosedHint: 'Regular session is closed. Tradier rejects market/day orders now — use a limit order with GTC, pre, or post.',
      day: 'Day',
      gtc: 'Good Til Cancelled',
      shortTerm: 'Short-term',
      longTerm: 'Long-term',
      // Quotes
      quotes: 'Quotes',
      symbol: 'Symbol',
      search: 'Symbol Search',
      searchPlaceholder: 'Search by name or ticker...',
      noResults: 'No results',
      bid: 'Bid',
      ask: 'Ask',
      last: 'Last',
      size: 'Size',
      halted: 'Halted',
      // Watchlists
      watchlists: 'Watchlists',
      noWatchlists: 'No watchlists found',
      // P&L
      gainLoss: 'Realized P&L',
      noGainLoss: 'No realized gain/loss records',
      openDate: 'Open Date',
      closeDate: 'Close Date',
      proceeds: 'Proceeds',
      cost: 'Cost',
      realizedPnl: 'Realized P&L',
      term: 'Term',
      pnlCalendar: 'P&L Calendar',
      // History
      history: 'Historical OHLCV',
      start: 'Start Date',
      end: 'End Date',
      interval: 'Interval',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      open: 'Open',
      high: 'High',
      low: 'Low',
      close: 'Close',
      volume: 'Volume',
      // Options
      options: 'Options',
      noOptions: 'No options data available',
      optionChain: 'Option Chain',
      optionSymbol: 'Symbol',
      strike: 'Strike',
      optBid: 'Bid',
      optAsk: 'Ask',
      optLast: 'Last',
      iv: 'IV',
      delta: 'Delta',
      gamma: 'Gamma',
      theta: 'Theta',
      vega: 'Vega',
      optVol: 'Vol',
      openInterest: 'OI',
      expiration: 'Expiry',
      calls: 'Calls',
      puts: 'Puts',
      showGreeks: 'Show Greeks',
      // Account modes
      paperMode: 'Paper',
      liveMode: 'Live',
      // Profile
      profile: 'Profile',
      name: 'Name',
      broker: 'Broker',
      accountType: 'Account Type',
      // Account History
      accountHistory: 'Account History',
      noAccountHistory: 'No account history',
      date: 'Date',
      amount: 'Amount',
      balance: 'Balance',
      description: 'Description',
      status: 'Status',
      entryType: 'Type',
      action: 'Action',
      stop: 'Stop',
      // Weekdays
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      exchange: 'Exchange',
      // Calendar
      calendar: 'Market Calendar',
      nextStateChange: 'Next State',
      calOpen: 'Open',
      calHalf: 'Half Day',
      calClosed: 'Closed',
      nextMonth: 'Next',
      prevMonth: 'Prev',
      // Watchlist extras
      emptyWatchlist: 'Empty',
      symbolCount: '{0} symbols',
      selectWatchlist: 'Select a watchlist',
      // Options extras
      allStrikes: 'All',
      // ETB
      etbCount: 'Showing {0} of {1}',
      filterPlaceholder: 'Filter symbols...',
      noEtbLoaded: 'not loaded',
      // P&L extras
      closedPositions: 'Closed Positions',
      totalPnl: 'Total P&L',
      noClosedPositions: 'No closed positions in {0}',
      // Misc
      add: 'Add',
      create: 'Create',
      delete: 'Delete',
      load: 'Load',
      apiError: 'API Error',
      connectionFailed: 'Connection failed',
      rateLimited: 'Rate limited',
      noApiKey: 'No API key configured',
      // 2026-06-22 build chain repair commit #3 — keys referenced via
      // `tt = t.components.tradier` in PositionGroupsTable.tsx and
      // MarketSessionBadge.tsx / MarketTape.tsx. The MarketSessionBadge
      // component reads marketSession as { label, states: {OPEN, ...} }.
      marketSession: {
        label: 'SESSION',
        states: {
          OPEN: 'Open',
          PRE_MARKET: 'Pre-market',
          AFTER_HOURS: 'After Hours',
          CLOSED: 'Closed',
          HALTED: 'Halted',
          UNKNOWN: 'Unknown',
        },
      },
      marketTape: {
        label: 'Tape',
        live: 'LIVE',
        stale: 'STALE',
        fear: 'FEAR',
        closed: 'closed',
      },
      positionGroups: 'Position Groups',
      positionGroupsEmpty: 'No position groups yet',
      positionGroupName: 'Name',
      positionGroupCount: 'Positions',
      positionGroupCreate: 'Create',
      positionGroupNamePlaceholder: 'Group name…',
      positionGroupActions: 'Actions',
    },
  },
  login: {
    brand: 'Quantitative Terminal',
    subtitle: 'black-purple operator console',
    signIn: 'Sign In',
    title: 'Dashboard Authentication',
    username: 'Username',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'signing in…',
    system: 'System',
    operational: 'OPERATIONAL',
    unconfigured: 'UNCONFIGURED',
    probing: 'probing',
    architecture: 'Architecture',
    model: 'Model',
    uptime: 'Uptime',
    governance: 'Governance Gates',
    market: 'Market',
    live: 'LIVE',
    offline: 'OFFLINE',
    stale: 'stale',
    closed: 'closed',
    changePasswordHint: 'change password from',
    inSettings: 'Settings',
    afterLogin: 'after login',
    rightsInternal: 'all rights internal',
    fear: 'FEAR',
  },
};
